import type {
  Navbar2Element,
  DreamValue,
  EmptyChildren,
} from '@typedream/core';
import { PlateRenderElementProps } from '@udecode/plate-core';
import styled from 'styled-components';
import { SpacerElement } from '@typedream/components';
import { DEFAULT_NAVBAR_HEIGHT, NAVBAR_POSITION } from '../../../constants';
import { useTPlateEditorRef } from '../../../plate';
import { NavbarMenuItemContext } from '../context/navbar.context';
import { NAVBAR_ALIGNMENT, NAVBAR_STYLE } from '../defaults';
import { NavbarBrandElementEditor } from './NavbarBrandElement';
import { MenuContainerElementEditor } from './NavbarMenuContainerElement';
import { NavbarDropdownOpenedMenuProvider } from '../context/navbarDropdownOpenedMenu.context';

const StyledDivContainer = styled.div<{
  isFixed: boolean,
  height: number,
}>`
  position: ${(props) => (props.isFixed ? 'fixed' : 'relative')};
  height: ${(props) => props.height}px;
  width: 100%;
  z-index: 2;
`;

const StyledNavbar = styled.nav<{
  isShadow: boolean,
  isBorder: boolean,
  isBlur: boolean,
  isStartOrEnd: boolean
}>`
  display: grid;
  position: relative;
  color: var(--text);
  background-color: var(--background);
  z-index: 30;
  overflow: visible;
  box-shadow: ${(props) => (props.isShadow ? 'var(--shadow-navbar)' : 'none')};
  border-bottom: ${(props) => (props.isBorder ? '1px solid var(--border-modal)' : 'none')};
  ${(props) => ((props.isBlur) ? `
    backdrop-filter: blur(5px);
    background-color: var(--background-blur) !important;
  ` : '')};
  grid-template-columns: ${(props) => (props.isStartOrEnd ? 'auto 1fr auto'
    : '1fr auto 1fr')};
`;

export function NavbarRootElementEditor(
  props: PlateRenderElementProps<DreamValue, Navbar2Element>,
) {
  const { element, attributes, children } = props;
  const editor = useTPlateEditorRef();

  return (
    <NavbarMenuItemContext.Provider
      value={props}
    >
      <NavbarDropdownOpenedMenuProvider>
        <StyledDivContainer
          className="editor-nav-container"
          isFixed={element.position === NAVBAR_POSITION.FIXED}
          height={DEFAULT_NAVBAR_HEIGHT}
          onMouseOver={() => editor.emitter.emit('toolbar:toggle-navbar-container-menu', { nav: element, state: true })}
          onMouseLeave={() => editor.emitter.emit('toolbar:toggle-navbar-container-menu', { nav: element, state: false })}
          // Prevent the navbar from being deleted using `backspace` key
          contentEditable={false}
          {...attributes}
        >
          <StyledNavbar
            isShadow={element.style === NAVBAR_STYLE.SHADOW}
            isBlur={element.isBlur ?? false}
            isBorder={element.style === NAVBAR_STYLE.BORDER}
            isStartOrEnd={([NAVBAR_ALIGNMENT.START, NAVBAR_ALIGNMENT.END] as Array<Navbar2Element['alignment']>).includes(element.alignment)}
            role="navigation"
            aria-label="main navigation"
            data-testid="Navbar"
          >
            <NavbarBrandElementEditor />
            <MenuContainerElementEditor type="middle" />
            <MenuContainerElementEditor type="end" />
          </StyledNavbar>
          {children}
        </StyledDivContainer>
        {element.position === NAVBAR_POSITION.FIXED && (
          <SpacerElement
            element={{
              id: 'fixed_navbar_spacer',
              type: 'spacer',
              height: DEFAULT_NAVBAR_HEIGHT,
              children: [{ text: '' }] as EmptyChildren,
            }}
          >
            <div />
          </SpacerElement>
        )}
      </NavbarDropdownOpenedMenuProvider>
    </NavbarMenuItemContext.Provider>
  );
}
